<template>
  <iframe
    v-if="calendarUrl"
    :src="calendarUrl"
    :style="frameStyles"
    frameborder="0"
    scrolling="no"
  ></iframe>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { GoogleCalendarProps, defaults } from './GoogleCalendar';

const props = withDefaults(defineProps<GoogleCalendarProps>(), {
  width: () => defaults.width,
  height: () => defaults.height,
  wkst: () => defaults.wkst,
  ctz: () => defaults.ctz,
  hl: () => defaults.hl,
  showTitle: () => defaults.showTitle,
  showNav: () => defaults.showNav,
  showPrint: () => defaults.showPrint,
  showDate: () => defaults.showDate,
  showTabs: () => defaults.showTabs,
  showCalendars: () => defaults.showCalendars,
  showTz: () => defaults.showTz,
});

const calendarUrl = computed(() => {
  if (!props.calendarId) return null;

  const params = new URLSearchParams();
  
  // Add calendar ID parameter
  params.append('src', props.calendarId);
  
  // Add height parameter
  if (props.height) {
    params.append('height', props.height);
  }
  
  // Add wkst parameter (first day of week)
  if (props.wkst !== undefined) {
    params.append('wkst', String(props.wkst));
  }
  
  // Add timezone
  if (props.ctz) {
    params.append('ctz', props.ctz);
  }
  
  // Add language
  if (props.hl) {
    params.append('hl', props.hl);
  }
  
  // Add mode if specified
  if (props.mode) {
    params.append('mode', props.mode);
  }
  
  // Add boolean flags as 0 or 1
  if (props.showTitle !== undefined) {
    params.append('showTitle', props.showTitle ? '1' : '0');
  }
  
  if (props.showNav !== undefined) {
    params.append('showNav', props.showNav ? '1' : '0');
  }
  
  if (props.showPrint !== undefined) {
    params.append('showPrint', props.showPrint ? '1' : '0');
  }
  
  if (props.showDate !== undefined) {
    params.append('showDate', props.showDate ? '1' : '0');
  }
  
  if (props.showTabs !== undefined) {
    params.append('showTabs', props.showTabs ? '1' : '0');
  }
  
  if (props.showCalendars !== undefined) {
    params.append('showCalendars', props.showCalendars ? '1' : '0');
  }
  
  if (props.showTz !== undefined) {
    params.append('showTz', props.showTz ? '1' : '0');
  }
  
  // Add color if specified
  if (props.color) {
    params.append('color', props.color);
  }
  
  return `https://calendar.google.com/calendar/embed?${params.toString()}`;
});

const frameStyles = computed(() => ({
  width: props.width,
  height: props.height,
}));
</script>
